export type HttpHeaders = Record<string, string>;

export interface HttpRequestConfig {
  baseURL?: string;
  url?: string;
  method?: string;
  headers?: HttpHeaders;
  params?: Record<string, unknown> | URLSearchParams;
  data?: unknown;
  withCredentials?: boolean;
  signal?: AbortSignal;
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer';
}

export interface HttpResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: HttpHeaders;
  config: HttpRequestConfig;
  isError: false;
}

export interface HttpErrorResponse<T = unknown> {
  isError: true;
  message: string;
  name: string;
  config?: HttpRequestConfig;
  response?: {
    data: T;
    status: number;
    statusText: string;
    headers: HttpHeaders;
  };
}

export type HttpResult<T = unknown> = HttpResponse<T> | HttpErrorResponse<T>;

type RequestInterceptor = (config: HttpRequestConfig) => HttpRequestConfig | Promise<HttpRequestConfig>;
type RequestErrorInterceptor = (error: unknown) => unknown;
type ResponseInterceptor = <T>(response: HttpResponse<T>) => HttpResult<T> | Promise<HttpResult<T>>;
type ResponseErrorInterceptor = <T>(error: HttpErrorResponse<T>) => HttpResult<T> | Promise<HttpResult<T>>;

interface InterceptorManager<T, E> {
  use: (fulfilled: T, rejected?: E) => number;
  eject: (id: number) => void;
}

export interface HttpInstance {
  defaults: Required<Pick<HttpRequestConfig, 'baseURL' | 'headers'>> & HttpRequestConfig;
  interceptors: {
    request: InterceptorManager<RequestInterceptor, RequestErrorInterceptor>;
    response: InterceptorManager<ResponseInterceptor, ResponseErrorInterceptor>;
  };
  request: <T = unknown>(config: HttpRequestConfig) => Promise<HttpResult<T>>;
  get: <T = unknown, R = HttpResult<T>>(url: string, config?: HttpRequestConfig) => Promise<R>;
  delete: <T = unknown, R = HttpResult<T>>(url: string, config?: HttpRequestConfig) => Promise<R>;
  post: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config?: HttpRequestConfig) => Promise<R>;
  put: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config?: HttpRequestConfig) => Promise<R>;
  patch: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config?: HttpRequestConfig) => Promise<R>;
}

function buildQueryString(params?: HttpRequestConfig['params']): string {
  if (!params) return '';
  if (params instanceof URLSearchParams) {
    const str = params.toString();
    return str ? `?${str}` : '';
  }
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => usp.append(key, String(v)));
    } else {
      usp.append(key, String(value));
    }
  });
  const str = usp.toString();
  return str ? `?${str}` : '';
}

function buildUrl(config: HttpRequestConfig): string {
  const { baseURL = '', url = '' } = config;
  const isAbsolute = /^https?:\/\//i.test(url);
  const base = isAbsolute ? '' : baseURL.replace(/\/+$/, '');
  const path = isAbsolute ? url : `${url.startsWith('/') ? '' : '/'}${url}`;
  return `${base}${path}${buildQueryString(config.params)}`;
}

function headersToObject(h: Headers): HttpHeaders {
  const obj: HttpHeaders = {};
  h.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
}

async function parseBody<T>(response: Response, responseType?: HttpRequestConfig['responseType']): Promise<T> {
  if (response.status === 204) return undefined as T;
  const type = responseType || 'json';
  if (type === 'text') return (await response.text()) as T;
  if (type === 'blob') return (await response.blob()) as T;
  if (type === 'arrayBuffer') return (await response.arrayBuffer()) as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as T;
    }
  }
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

function createInterceptorManager<T, E>() {
  const handlers: Array<{ fulfilled: T | null; rejected: E | null } | null> = [];
  return {
    use(fulfilled: T, rejected?: E) {
      handlers.push({ fulfilled, rejected: rejected ?? null });
      return handlers.length - 1;
    },
    eject(id: number) {
      if (handlers[id]) handlers[id] = null;
    },
    forEach(cb: (h: { fulfilled: T | null; rejected: E | null }) => void) {
      handlers.forEach((h) => h && cb(h));
    },
  };
}

export function createHttpInstance(defaults: HttpRequestConfig = {}): HttpInstance {
  const requestInterceptors = createInterceptorManager<RequestInterceptor, RequestErrorInterceptor>();
  const responseInterceptors = createInterceptorManager<ResponseInterceptor, ResponseErrorInterceptor>();

  const resolvedDefaults = {
    baseURL: defaults.baseURL ?? '',
    headers: defaults.headers ?? {},
    ...defaults,
  };

  async function request<T>(userConfig: HttpRequestConfig): Promise<HttpResult<T>> {
    let config: HttpRequestConfig = {
      ...resolvedDefaults,
      ...userConfig,
      headers: { ...resolvedDefaults.headers, ...(userConfig.headers ?? {}) },
    };

    try {
      const pending: RequestInterceptor[] = [];
      requestInterceptors.forEach((h) => {
        if (h.fulfilled) pending.push(h.fulfilled);
      });
      // eslint-disable-next-line no-restricted-syntax
      for (const interceptor of pending) {
        // eslint-disable-next-line no-await-in-loop
        config = await interceptor(config);
      }
    } catch (e) {
      const err: HttpErrorResponse<T> = {
        isError: true,
        message: e instanceof Error ? e.message : String(e),
        name: 'RequestInterceptorError',
        config,
      };
      return err;
    }

    const finalUrl = buildUrl(config);
    const method = (config.method ?? 'GET').toUpperCase();

    const headers: HttpHeaders = { ...(config.headers ?? {}) };
    let body: BodyInit | undefined;
    if (config.data !== undefined && config.data !== null) {
      if (
        (typeof FormData !== 'undefined' && config.data instanceof FormData) ||
        (typeof Blob !== 'undefined' && config.data instanceof Blob) ||
        config.data instanceof ArrayBuffer ||
        config.data instanceof URLSearchParams ||
        typeof config.data === 'string'
      ) {
        body = config.data as BodyInit;
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      } else {
        body = JSON.stringify(config.data);
        if (!headers['Content-Type'] && !headers['content-type']) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    let fetchResponse: Response;
    try {
      fetchResponse = await fetch(finalUrl, {
        method,
        headers,
        body,
        credentials: config.withCredentials ? 'include' : 'same-origin',
        signal: config.signal,
      });
    } catch (e) {
      const err: HttpErrorResponse<T> = {
        isError: true,
        message: e instanceof Error ? e.message : 'Network Error',
        name: e instanceof Error ? e.name : 'NetworkError',
        config,
      };
      let result: HttpResult<T> = err;
      const pending: ResponseErrorInterceptor[] = [];
      responseInterceptors.forEach((h) => {
        if (h.rejected) pending.push(h.rejected);
      });
      // eslint-disable-next-line no-restricted-syntax
      for (const interceptor of pending) {
        // eslint-disable-next-line no-await-in-loop
        result = await interceptor(result as HttpErrorResponse<T>);
        if (!result.isError) break;
      }
      return result;
    }

    const responseHeaders = headersToObject(fetchResponse.headers);

    if (!fetchResponse.ok) {
      let data: T;
      try {
        data = await parseBody<T>(fetchResponse, config.responseType);
      } catch {
        data = undefined as T;
      }
      const err: HttpErrorResponse<T> = {
        isError: true,
        message: `Request failed with status code ${fetchResponse.status}`,
        name: 'HttpError',
        config,
        response: {
          data,
          status: fetchResponse.status,
          statusText: fetchResponse.statusText,
          headers: responseHeaders,
        },
      };
      let result: HttpResult<T> = err;
      const pending: ResponseErrorInterceptor[] = [];
      responseInterceptors.forEach((h) => {
        if (h.rejected) pending.push(h.rejected);
      });
      // eslint-disable-next-line no-restricted-syntax
      for (const interceptor of pending) {
        // eslint-disable-next-line no-await-in-loop
        result = await interceptor(result as HttpErrorResponse<T>);
        if (!result.isError) break;
      }
      return result;
    }

    const data = await parseBody<T>(fetchResponse, config.responseType);
    let result: HttpResult<T> = {
      data,
      status: fetchResponse.status,
      statusText: fetchResponse.statusText,
      headers: responseHeaders,
      config,
      isError: false,
    };

    const pending: ResponseInterceptor[] = [];
    responseInterceptors.forEach((h) => {
      if (h.fulfilled) pending.push(h.fulfilled);
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const interceptor of pending) {
      // eslint-disable-next-line no-await-in-loop
      result = await interceptor(result as HttpResponse<T>);
      if (result.isError) break;
    }
    return result;
  }

  const instance: HttpInstance = {
    defaults: resolvedDefaults as HttpInstance['defaults'],
    interceptors: {
      request: requestInterceptors as unknown as HttpInstance['interceptors']['request'],
      response: responseInterceptors as unknown as HttpInstance['interceptors']['response'],
    },
    request: <T = unknown>(config: HttpRequestConfig) => request<T>(config),
    get: <T = unknown, R = HttpResult<T>>(url: string, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method: 'GET' }) as unknown as Promise<R>,
    delete: <T = unknown, R = HttpResult<T>>(url: string, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method: 'DELETE' }) as unknown as Promise<R>,
    post: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method: 'POST', data }) as unknown as Promise<R>,
    put: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method: 'PUT', data }) as unknown as Promise<R>,
    patch: <T = unknown, R = HttpResult<T>>(url: string, data?: unknown, config: HttpRequestConfig = {}) =>
      request<T>({ ...config, url, method: 'PATCH', data }) as unknown as Promise<R>,
  };

  return instance;
}

export type AxiosResponse<T = unknown> = HttpResponse<T>;
export type AxiosError<T = unknown> = HttpErrorResponse<T>;
export type AxiosRequestConfig = HttpRequestConfig;
